import { pileThickness } from '../../../utils/game/cardMesh';
// Presentation-invariant layer. The twin of renderParity, one level further
// out. renderParity asserts the render BOOKKEEPING matches the mirror (does a
// play-zone card have a mesh, does its instance carry the authoritative
// rotated/tapped flags). This module asserts the PICTURE: the face, yaw, art
// and stack heights the player actually looks at, read from the scene digest
// (`describeScene`. Real mesh geometry, the texture really applied, live DOM)
// and compared against what the server projection says they must be.
//
// The split matters because the two failure classes are disjoint. A card can
// carry a perfectly authoritative `rotated: false` and still render at yaw 180
// (a mesh spawned from the wrong seat's orientation), and an owner-locked spell
// can be correctly present in the unit hand and still show a card BACK to the
// seat entitled to its face. Both were live bugs; neither is visible to a
// state-derived check, which is exactly why nothing here reads game state.
//
//   P1 face. Mirror faceDown=false → the mesh shows its FRONT (and the
//                    front texture is real art, not a card back); faceDown=true
//                    and unrevealed to this viewer → it shows its BACK.
//   P2 orientation. The mesh yaw carries the 180° component iff the mirror
//                    says the card is p2-controlled. The violation names what
//                    the VIEWER perceives (camera-rotated seats read 180 as
//                    upright), which is the language the bug arrives in:
//                    "that card looks like it belongs to my opponent".
//   P3 tap visual. The mesh yaw carries the 90° quarter-turn iff the mirror
//                    says the card is tapped.
//   P4 piles. A rendered stack is exactly as tall as the mirror's zone
//                    for that seat, and a non-empty cemetery shows a real face
//                    on top, never a card back.
//   P5 unit hands. Every owner-locked spell in a badge the viewer is
//                    entitled to see shows FRONT art. The controller is looking
//                    at their OWN spells; a back there is always a render bug.
//
// Pure: (sceneDigest, mirrorState, viewer) → string[] of violations. Each
// string starts with a stable `kind:id` key, matching renderParity's format.

// Board yaw is composed from four contributions and nothing else. See
// cardMesh.createCardMesh (`baseZ`) and animations.computeTargetZ, which must
// stay identical. Expressed in normalized degrees so it compares directly
// against the digest's `yawBucket`.
export function expectedYawDeg({ isSite, rotated, faceDown, tapped }) {
  let z = 0;
  if (isSite) z -= 90;
  if (rotated) z += 180;
  if (faceDown) z += 180;
  // Site tap turns toward upright portrait (+90), unit tap the usual -90.
  // mirrors cardMesh/animations exactly.
  if (tapped) z += isSite ? 90 : -90;
  return ((z % 360) + 360) % 360;
}

// A rendered card back, by texture filename (cardback-spellbook-rounded.png /
// cardback-atlas-rounded.png. BoardLifecycle's setCardBackUrls and
// UnitHandBadges' cardBackSrc both resolve to these).
function isBackArt(src) {
  return /^cardback[-.]/i.test(String(src || ''));
}

// Digest pile name → the mirror zone key it renders. Banished and Collection
// never get a table mesh (createPileMesh returns null), so they can't appear.
const PILE_ZONE = {
  Spellbook: 'spellbook',
  Atlas: 'atlas',
  'Cemetery P1': 'cemetery',
  'Cemetery P2': 'cemetery',
  Cemetery: 'cemetery',
};

// The seats whose private information this client holds. Solo owns both, an
// online client only its own. A face is expected to be visible only for these.
function visibleSeats(viewer) {
  if (viewer?.isSolo) return ['p1', 'p2'];
  return [viewer?.localSeat === 'p2' ? 'p2' : 'p1'];
}

// Does the ACTUAL yaw carry the 180° side-flip, given the card's other
// (site/tap/face) contributions? Exact. Decomposing by inspection would
// mis-read a tapped site (−90 −90 = 180) as p2-controlled.
function has180(actualDeg, flags) {
  const base = expectedYawDeg({ ...flags, rotated: false });
  return (((actualDeg - base) % 360) + 360) % 360 === 180;
}

function mirrorPileCount(mirrorState, seat, zone) {
  const arr = mirrorState?.piles?.[seat]?.[zone];
  if (Array.isArray(arr)) return arr.length;
  const n = mirrorState?.pileCounts?.[seat]?.[zone];
  return typeof n === 'number' ? n : null;
}

// The Howl-from-Beyond reveal meshes are cosmetic (`<fxId>-reveal-<n>`). They
// carry no mirror card entry, so their expected orientation comes from the fx
// manifest's caster seat instead. Returns the pseudo-mirror-card the board
// checks run against, or null when the mesh isn't a reveal.
function fxRevealCard(id, mirrorState) {
  for (const fx of mirrorState?.boardFx || []) {
    if (!fx?.id || !String(id).startsWith(`${fx.id}-reveal-`)) continue;
    return { cardId: fx.id, rotated: fx.seat === 'p2', faceDown: false, tapped: false, isSite: false };
  }
  return null;
}

// P6. Presentation queue. During a combat strike playback the scene shows an
// earlier committed version than the authoritative mirror ON PURPOSE, and the
// checks above run against that displayed version. Two things must still hold:
// the waiting commits are contiguous from the displayed version (nothing was
// dropped), and once nothing is playing and nothing is queued the displayed
// version IS the authoritative one (a stale view is never left silently).
export function checkPresentationQueue(combat) {
  const p = combat?.presentation;
  if (!p) return [];
  const out = [];
  if (!p.contiguous) out.push(`presentation-queue:gap queued commits are not contiguous from displayed version ${p.displayedVersion}`);
  const authoritative = combat.authoritativeVersion;
  if (!p.busy && p.queued === 0 && typeof authoritative === 'number' && p.displayedVersion !== authoritative) {
    out.push(`presentation-stale:${p.displayedVersion} displayed version lags authoritative ${authoritative} with nothing playing or queued`);
  }
  return out;
}

export function checkPresentation(scene, mirrorState, viewer = {}) {
  const violations = [];
  if (!scene || !mirrorState) return violations;
  violations.push(...checkPresentationQueue(scene.combat));
  const seats = visibleSeats(viewer);
  const cameraRotated = !!viewer.cameraRotated;
  const cards = mirrorState.cards || {};

  // ── P1 / P2 / P3. Per-board-card face, orientation, tap ──────────────
  for (const entry of scene.board || []) {
    const mc = cards[entry.id]?.zone === 'play' ? cards[entry.id] : fxRevealCard(entry.id, mirrorState);
    if (!mc) continue; // no authoritative opinion. Nothing to assert against
    const flags = {
      isSite: !!mc.isSite,
      rotated: !!mc.rotated,
      faceDown: !!mc.faceDown,
      tapped: !!mc.tapped,
    };
    const revealed = (mc.revealedTo || []).some((s) => seats.includes(s));

    // P1. Face.
    let faceBad = false;
    if (!flags.faceDown && entry.face !== 'front') {
      violations.push(`face:${entry.id} server faceDown=false but the mesh renders its BACK`);
      faceBad = true;
    } else if (flags.faceDown && !revealed && entry.face !== 'back') {
      violations.push(`face:${entry.id} server faceDown=true (unrevealed) but the mesh renders its FRONT`);
      faceBad = true;
    }
    // A face-up card whose front texture IS a card back. The identity is
    // known to the projection, so the art resolution failed, not the entitlement.
    if (!flags.faceDown && mc.cardId != null && entry.face === 'front' && isBackArt(entry.shownArt)) {
      violations.push(`face-art:${entry.id} face-up card renders card-back art (${entry.shownArt})`);
    }

    // P2 / P3. Yaw. Skipped when the face is already wrong: a bad face
    // contributes its own 180° and would double-report as an orientation bug.
    if (faceBad || typeof entry.yawBucket !== 'number') continue;
    const want = expectedYawDeg(flags);
    if (entry.yawBucket === want) continue;
    if (entry.yawBucket === expectedYawDeg({ ...flags, rotated: !flags.rotated })) {
      const readsAsOpponent = has180(entry.yawBucket, flags) !== cameraRotated;
      violations.push(`orientation:${entry.id} renders yaw ${entry.yawBucket}°. Reads as the `
        + `${readsAsOpponent ? "OPPONENT's" : "viewer's own"} card, but server rotated=${flags.rotated} `
        + `expects yaw ${want}°`);
    } else if (entry.yawBucket === expectedYawDeg({ ...flags, tapped: !flags.tapped })) {
      violations.push(`tap-visual:${entry.id} renders yaw ${entry.yawBucket}° (${flags.tapped ? 'untapped' : 'tapped'} `
        + `quarter-turn) but server tapped=${flags.tapped} expects yaw ${want}°`);
    } else {
      violations.push(`yaw:${entry.id} renders yaw ${entry.yawBucket}° but the server flags `
        + `(site=${flags.isSite} rotated=${flags.rotated} faceDown=${flags.faceDown} tapped=${flags.tapped}) expect ${want}°`);
    }
  }

  // ── P4. Pile stacks ──────────────────────────────────────────────────
  for (const pile of scene.piles || []) {
    const zone = PILE_ZONE[pile.name];
    if (!zone) continue;
    // The rendered side IS the render truth: p1 stacks sit at yaw 0 (Spellbook /
    // Cemetery) or 270 (Atlas, the site quarter-turn), p2 at their +180.
    const seat = (pile.yawDeg === 180 || pile.yawDeg === 90) ? 'p2' : 'p1';
    // An opponent's hidden piles are only count-folded for Spellbook/Atlas.
    // their cemetery is not reconciled from counts, so it isn't derivable.
    if (!seats.includes(seat) && zone === 'cemetery') continue;
    const count = mirrorPileCount(mirrorState, seat, zone);
    if (count != null && typeof pile.renderedDepth === 'number') {
      if (Math.abs(pile.renderedDepth - pileThickness(count)) > 0.0001) {
        violations.push(`pile-height:${seat}:${zone} ${pile.name} depth ${pile.renderedDepth} does not match ${count} cards`);
      }
    } else if (count != null && typeof pile.renderedCount === 'number' && pile.renderedCount !== count) {
      violations.push(`pile-height:${seat}:${zone} ${pile.name} renders ${pile.renderedCount} cards but the server holds ${count}`);
    }
    if (zone === 'cemetery' && count > 0 && isBackArt(pile.topArt)) {
      violations.push(`cemetery-top:${seat} non-empty cemetery (${count}) shows card-back art on top (${pile.topArt})`);
    }
  }

  // ── P5. Unit-hand badges ─────────────────────────────────────────────
  for (const badge of scene.unitHands || []) {
    const entry = mirrorState.unitHands?.[badge.bearer];
    if (!entry) continue; // badge outliving its hand (mid-drag). Not our call
    if (!seats.includes(entry.controller)) continue; // opponent's hand: backs are correct
    const faced = new Set((entry.spells || []).filter((s) => !s?.faceDown).map((s) => s?.id));
    for (const card of badge.cards || []) {
      if (!faced.has(card.id)) continue; // ghost / stale slot the projection doesn't back
      if (isBackArt(card.shownArt)) {
        violations.push(`unit-hand-back:${badge.bearer}:${card.id} owner-locked spell shows card-back art `
          + `(${card.shownArt}) to its controller (${entry.controller})`);
      }
    }
    if (isBackArt(badge.ownerArt)) {
      violations.push(`unit-hand-badge-art:${badge.bearer} unit-hand badge shows card-back art (${badge.ownerArt}) instead of the owner unit`);
    }
  }

  return violations;
}
